/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { appendSizeProperties } from '@theme/line-item-size';

/**
 * Collects every section id present on the page so the cart drawer (and any
 * other cart sections) can be re-rendered as part of the add-to-cart fetch.
 * @returns {string[]}
 */
const getSectionsToRender = () => {
  /** @type {string[]} */
  const sections = [];
  for (const element of document.querySelectorAll('[data-section-id]')) {
    const sectionId = element.getAttribute('data-section-id');
    if (sectionId && !sections.includes(sectionId)) sections.push(sectionId);
  }
  return sections;
};

/**
 * Per-card upsell add-to-cart. Listens for either:
 *   - `change` on a `<select data-upsell-select>` (multi-size cards)
 *   - `click`  on a `<button data-upsell-add data-variant-id="…">` (single-variant)
 *
 * Posts directly to `/cart/add.js` and dispatches `CartAddEvent` so
 * `cart-items-component` re-renders the drawer.
 */
class CartUpsellForm extends HTMLElement {
  /** @type {boolean} */
  #adding = false;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-upsell-select]')) return;
    const variantId = select.value;
    if (!variantId) return;
    this.#addToCart(variantId, select);
    // Reset to the placeholder so the next interaction starts from "Choose".
    select.selectedIndex = 0;
  };

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = /** @type {HTMLButtonElement | null} */ (event.target.closest('button[data-upsell-add]'));
    if (!button) return;
    this.#addToCart(button.dataset.variantId, button);
  };

  /**
   * @param {string | undefined} variantId
   * @param {HTMLElement} control - The control that triggered the add — the
   *  <select> on multi-size cards, or the button itself on single-variant.
   */
  async #addToCart(variantId, control) {
    if (!variantId || this.#adding) return;
    this.#adding = true;
    control.setAttribute('disabled', '');

    // The visible "Add"/"Choose" button — same node as control on the
    // single-variant path; sibling in `.cart-upsell-item__action` on multi-size.
    const button =
      control instanceof HTMLButtonElement
        ? control
        : control.closest('.cart-upsell-item__action')?.querySelector('.cart-upsell-item__add');

    // Show the spinner immediately so the user gets feedback while the
    // fetch is in flight. The cart re-render usually removes this card
    // entirely once the add lands; the loading state only matters for the
    // brief in-flight window (or for error cases where the card stays).
    if (button instanceof HTMLElement) {
      button.dataset.state = 'loading';
    }

    try {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');
      appendSizeProperties(body, variantId, control);

      // Set by the cart item upsell modal — traces the line back to the
      // question that recommended it, so its answer can be cleared if the
      // line is removed. Unset for the regular "you may also like" carousel.
      const upsellSource = this.dataset.upsellSource;
      if (upsellSource) body.append('properties[_upsell_type]', upsellSource);

      const sections = getSectionsToRender();
      if (sections.length > 0) body.append('sections', sections.join(','));

      const url = /** @type {any} */ (window).Theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(url, {
        ...fetchConfig('javascript', { body }),
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();

      if (data.status) return; // Shopify error response

      this.dispatchEvent(
        new CartAddEvent({}, variantId, { source: 'cart-upsells', itemCount: 1, sections: data.sections }),
      );
    } catch {
      // Network / parse failure — leave the control re-enabled for a retry.
    } finally {
      this.#adding = false;
      control.removeAttribute('disabled');
      if (button instanceof HTMLElement) {
        button.removeAttribute('data-state');
      }
    }
  }
}

if (!customElements.get('cart-upsell-form')) {
  customElements.define('cart-upsell-form', CartUpsellForm);
}
